import * as Sentry from '@sentry/react'
import {
  isError,
  isErrorWithMessage as isErrorWithMsg,
  isErrorWithReason,
  isAxiosError,
} from './errorGuards'

export enum ErrorNames {
  SHIFT_EDIT_WINDOW_CLOSED = 'ShiftEditWindowClosedError',
}

type ErrorWithMessage = {
  message: string
}

/**
 * HTTP status codes that represent expected auth failures (expired sessions,
 * revoked tokens) rather than actionable bugs. Reporting these to Sentry is
 * pure noise — the frontend already handles them via redirect-to-login.
 */
const IGNORED_HTTP_STATUS_CODES = new Set([401, 403])

// The ops/biz API interceptors throw `error.response.data` (the unwrapped Traba
// error body), which has NO `.response.status` / `.status` — only `errorType`.
// So a 401/403 reaches here with no HTTP status; match on the body's errorType
// too, otherwise auth/permission denials (e.g. onsite ONSITE_*_DENIED) flood
// Sentry as un-actionable noise the FE already surfaces to the user.
const IGNORED_TRABA_ERROR_TYPES = new Set(['UNAUTHORIZED', 'FORBIDDEN'])

function getTrabaErrorType(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const errorType = (error as Record<string, unknown>)['errorType']
  return typeof errorType === 'string' ? errorType : undefined
}

function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const e = error as Record<string, unknown>
  // Axios-style: error.response.status
  if (
    e['response'] &&
    typeof e['response'] === 'object' &&
    'status' in (e['response'] as Record<string, unknown>)
  ) {
    const status = (e['response'] as Record<string, unknown>)['status']
    if (typeof status === 'number') {
      return status
    }
  }
  // NestJS HttpException-style: error.status
  if (typeof e['status'] === 'number') {
    return e['status']
  }
  return undefined
}

export function captureSentryError(
  error: unknown,
  options?: Record<string, unknown>,
) {
  const status = getHttpStatus(error)
  if (status !== undefined && IGNORED_HTTP_STATUS_CODES.has(status)) {
    return
  }

  const trabaErrorType = getTrabaErrorType(error)
  if (
    trabaErrorType !== undefined &&
    IGNORED_TRABA_ERROR_TYPES.has(trabaErrorType)
  ) {
    return
  }

  if (error instanceof Error) {
    Sentry.captureException(error, options)
  } else {
    // Capture non-Error exceptions with extra context
    // Sentry.captureException can handle non-Error values, but wrapping
    // in an Error provides better stack traces and error messages
    const errorMessage = getErrorMessage(error)
    const wrappedError = new Error(errorMessage)
    wrappedError.name = 'NonErrorException'

    Sentry.captureException(wrappedError, {
      ...options,
      extra: {
        ...((options?.['extra'] as Record<string, unknown>) ?? {}),
        originalError: error,
      },
    })
  }
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>)['message'] === 'string'
  )
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) {
    return maybeError
  }

  try {
    return new Error(JSON.stringify(maybeError))
  } catch {
    return new Error(String(maybeError))
  }
}

/**
 * Extract a user-friendly error message from any error type.
 * Handles standard Errors, Axios errors, objects with message/reason properties, etc.
 *
 * Priority order:
 * 1. Axios response data (most specific API errors)
 * 2. Standard Error instance
 * 3. Object with message property
 * 4. Object with reason property
 * 5. String errors
 * 6. Fallback serialization
 */
export function getErrorMessage(error: unknown): string {
  // Handle string errors early (common case)
  if (typeof error === 'string') {
    return error
  }

  // Handle Axios errors first - API responses are most specific
  if (isAxiosError(error)) {
    const data = error.response?.data

    // Priority 1: Response data with message
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message: unknown }).message
      if (typeof message === 'string' && message) {
        return message
      }
    }

    if (data && typeof data === 'object' && 'error' in data) {
      const apiError = (data as { error: unknown }).error
      if (typeof apiError === 'string' && apiError) {
        return apiError
      }
    }

    // Priority 2: String response data
    if (typeof data === 'string' && data) {
      return data
    }

    // Priority 3: Error message from Axios error (skip generic status text)
    if (
      error.message &&
      !/^Request failed with status code \d+$/.test(error.message)
    ) {
      return error.message
    }
  }

  // Handle standard Error instances
  if (isError(error)) {
    return error.message
  }

  // Handle objects with message property
  if (isErrorWithMsg(error)) {
    return error.message
  }

  // Handle objects with reason property (common in some APIs)
  if (isErrorWithReason(error)) {
    return error.reason
  }

  // Fallback: Try to serialize the error
  return toErrorWithMessage(error).message
}

/**
 * Get detailed error information including message, stack trace, and context.
 * Useful for logging and debugging.
 */
export function getErrorDetails(error: unknown): {
  message: string
  stack?: string
  name?: string
  cause?: unknown
  statusCode?: number
  data?: unknown
} {
  const message = getErrorMessage(error)

  // Standard Error
  if (isError(error)) {
    return {
      message,
      stack: error.stack,
      name: error.name,
      // Note: error.cause requires ES2022+ target
      cause:
        'cause' in error
          ? (error as Error & { cause?: unknown }).cause
          : undefined,
    }
  }

  // Axios Error
  if (isAxiosError(error)) {
    return {
      message,
      stack: error.message,
      statusCode: error.response?.status,
      data: error.response?.data,
    }
  }

  // Unknown error - try to extract what we can
  return {
    message,
    data: error,
  }
}

export function assertUnreachable(x: never): never {
  throw new Error(`Unreachable case: ${x}`)
}

interface BatchError {
  response: {
    message: Array<{
      status: 'fulfilled' | 'rejected'
      reason?: {
        message: string
      }
    }>
  }
}

export function isBatchError(error: unknown): error is BatchError {
  return (
    !!error &&
    typeof error === 'object' &&
    'response' in error &&
    !!error.response &&
    typeof error.response === 'object' &&
    'message' in error.response &&
    Array.isArray(error.response.message)
  )
}

export function parseBatchResults(
  messages: Array<{ status: string; reason?: { message: string } }>,
): {
  failedCount: number
  failedMessages: string[]
  successCount: number
} {
  const failedMessages = messages
    .filter((m) => m.status === 'rejected')
    .map((m) => m.reason?.message)
    .filter((msg): msg is string => !!msg)

  return {
    failedCount: failedMessages.length,
    failedMessages,
    successCount: messages.filter((m) => m.status === 'fulfilled').length,
  }
}

export enum TrabaErrorType {
  DYNAMIC_SHIFT_FILLING = 'DYNAMIC_SHIFT_FILLING',
  SHIFT_READINESS = 'SHIFT_READINESS',
  WORKER_INCENTIVE = 'WORKER_INCENTIVE',
  WORKERS_COMP = 'WORKERS_COMP',
}

/** Error codes for worker incentive API errors (must match backend WorkerIncentiveErrorCode). */
export enum WorkerIncentiveErrorCode {
  W2_WORKER_MISSING_STRIPE_ACCOUNT_FOR_1099_SHIFT_INCENTIVE = 'W2_WORKER_MISSING_STRIPE_ACCOUNT_FOR_1099_SHIFT_INCENTIVE',
  CONTRACTOR_1099_WORKER_MISSING_ZEAL_EMPLOYEE_ID_FOR_W2_SHIFT_INCENTIVE = 'CONTRACTOR_1099_WORKER_MISSING_ZEAL_EMPLOYEE_ID_FOR_W2_SHIFT_INCENTIVE',
}

const WORKER_INCENTIVE_ERROR_CODES = new Set<string>(
  Object.values(WorkerIncentiveErrorCode),
)

interface WorkerIncentiveErrorResponse {
  errorType: TrabaErrorType.WORKER_INCENTIVE
  errorCode: WorkerIncentiveErrorCode
}

const WORKER_INCENTIVE_ERROR_MESSAGES: Record<
  WorkerIncentiveErrorCode,
  string
> = {
  [WorkerIncentiveErrorCode.W2_WORKER_MISSING_STRIPE_ACCOUNT_FOR_1099_SHIFT_INCENTIVE]:
    'This worker is W2 but the shift is 1099. They need a connected Stripe account to receive this incentive.',
  [WorkerIncentiveErrorCode.CONTRACTOR_1099_WORKER_MISSING_ZEAL_EMPLOYEE_ID_FOR_W2_SHIFT_INCENTIVE]:
    'This worker is 1099 but the shift is W2. They need a Zeal employee ID to receive this incentive.',
}

function isWorkerIncentiveErrorCode(
  errorCode: unknown,
): errorCode is WorkerIncentiveErrorCode {
  return (
    typeof errorCode === 'string' && WORKER_INCENTIVE_ERROR_CODES.has(errorCode)
  )
}

function isWorkerIncentiveErrorResponse(
  data: unknown,
): data is WorkerIncentiveErrorResponse {
  if (!data || typeof data !== 'object') {
    return false
  }

  const parsedData = data as Record<string, unknown>
  return (
    parsedData['errorType'] === TrabaErrorType.WORKER_INCENTIVE &&
    isWorkerIncentiveErrorCode(parsedData['errorCode'])
  )
}

/**
 * Returns a user-friendly message for worker incentive errors when the backend
 * returns errorType WORKER_INCENTIVE and a known errorCode; otherwise returns
 * getErrorMessage(error).
 */
export function getWorkerIncentiveErrorMessage(error: unknown): string {
  const data = isAxiosError(error) ? error.response?.data : undefined
  if (isWorkerIncentiveErrorResponse(data)) {
    return WORKER_INCENTIVE_ERROR_MESSAGES[data.errorCode]
  }

  return getErrorMessage(error)
}

/** Error codes for workers comp coverage gating (must match backend WorkersCompErrorCodes). */
export enum WorkersCompErrorCode {
  NO_WORKERS_COMP_COVERAGE = 'no_workers_comp_coverage',
}

const WORKERS_COMP_ERROR_CODES = new Set<string>(
  Object.values(WorkersCompErrorCode),
)

interface WorkersCompErrorResponse {
  errorType: TrabaErrorType.WORKERS_COMP
  errorCode: WorkersCompErrorCode
}

function isWorkersCompErrorCode(
  errorCode: unknown,
): errorCode is WorkersCompErrorCode {
  return (
    typeof errorCode === 'string' && WORKERS_COMP_ERROR_CODES.has(errorCode)
  )
}

function isWorkersCompErrorResponse(
  data: unknown,
): data is WorkersCompErrorResponse {
  if (!data || typeof data !== 'object') {
    return false
  }

  const parsedData = data as Record<string, unknown>
  return (
    parsedData['errorType'] === TrabaErrorType.WORKERS_COMP &&
    isWorkersCompErrorCode(parsedData['errorCode'])
  )
}

/**
 * True when the backend rejected a shift post because a W2 (location, role
 * family) lacks active workers comp coverage (errorType WORKERS_COMP, errorCode
 * no_workers_comp_coverage).
 */
export function isWorkersCompCoverageError(error: unknown): boolean {
  // trabaApi response interceptors rethrow the Nest error body directly, so the
  // WC payload can arrive as an axios error's response.data or as the error
  // itself. isWorkersCompErrorResponse guards the shape either way.
  const data = isAxiosError(error) ? error.response?.data : error
  return isWorkersCompErrorResponse(data)
}

export function isTrabaError(error: unknown): error is {
  errorType: TrabaErrorType
  errorCode: string
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errorCode' in error &&
    'errorType' in error
  )
}
